import { reportPlaywireAdTelemetry } from "@/lib/playwireAdTelemetry";

export type LobbyAdLayout = "bottom" | "railNarrow" | "railWide";

export const PLAYWIRE_LOBBY_RETRY_DELAY_MS = 5_000;
export const PLAYWIRE_LOBBY_READY_TIMEOUT_MS = 15_000;

// Temporary wide-screen fallback while Playwire creates a dedicated /play
// placement. This intentionally shares the Players placement's reporting.
export const PLAYWIRE_LOBBY_RAIL_UNIT = "standard_iab_rght2";

type PlaywireSpaAds = (options: {
  ads: Array<{ selectorId: string; type: string }>;
  countPageview: boolean;
  path: string;
}) => Promise<unknown> | unknown;

type Timer = ReturnType<typeof setTimeout>;

export function hasRenderedPlaywireAd(slot: HTMLElement | null) {
  // Playwire inserts its data-pw-desk wrapper even when GPT returns no ad.
  // A rendered display ad has an iframe; the empty wrapper is display:none
  // with a zero-height container and must still be eligible for our retry.
  return Boolean(slot?.querySelector("iframe"));
}

interface PlaywireLobbyAdDependencies {
  getSpaAds: () => PlaywireSpaAds | null;
  enqueueWhenReady: (callback: () => void) => boolean;
  hasMountedAd: (selectorId: string) => boolean;
  schedule: (callback: () => void, delayMs: number) => Timer;
  cancel: (timer: Timer) => void;
  now: () => number;
  report: typeof reportPlaywireAdTelemetry;
  warn: (message: string, details: Record<string, unknown>) => void;
}

const browserDependencies: PlaywireLobbyAdDependencies = {
  getSpaAds: () => {
    if (typeof window === "undefined") return null;
    const spaAds = window.ramp?.spaAds;
    return typeof spaAds === "function" ? spaAds.bind(window.ramp) : null;
  },
  enqueueWhenReady: (callback) => {
    if (typeof window === "undefined") return false;
    window.ramp = window.ramp || {};
    window.ramp.que = window.ramp.que || [];
    if (typeof window.ramp.que.push !== "function") return false;
    window.ramp.que.push(callback);
    return true;
  },
  hasMountedAd: (selectorId) => {
    if (typeof document === "undefined") return false;
    return hasRenderedPlaywireAd(document.getElementById(selectorId));
  },
  schedule: (callback, delayMs) => setTimeout(callback, delayMs),
  cancel: (timer) => clearTimeout(timer),
  now: () => Date.now(),
  report: reportPlaywireAdTelemetry,
  warn: (message, details) => console.warn(message, details),
};

export function hasLobbySideAd(adLayout: LobbyAdLayout | null) {
  return adLayout === "railNarrow" || adLayout === "railWide";
}

export function getLobbyAdLayout(hasWideRail: boolean): LobbyAdLayout {
  return hasWideRail ? "railWide" : "bottom";
}

export function getLobbyPlaywireUnit(adLayout: LobbyAdLayout) {
  if (hasLobbySideAd(adLayout)) {
    return {
      selectorId: "lobby_skyscraper_right",
      type: PLAYWIRE_LOBBY_RAIL_UNIT,
    };
  }

  return {
    selectorId: "lobby_bottom",
    type: "leaderboard_atf",
  };
}

export function createLobbyPlaywireAdController(
  dependencies: PlaywireLobbyAdDependencies = browserDependencies,
) {
  let cachedSpaAds: PlaywireSpaAds | null = null;

  const request = (adLayout: LobbyAdLayout, countPageview: boolean) => {
    const spaAds = dependencies.getSpaAds() || cachedSpaAds;
    if (!spaAds) return false;
    cachedSpaAds = spaAds;

    try {
      const result = spaAds({
        ads: [getLobbyPlaywireUnit(adLayout)],
        countPageview,
        path: "ROS",
      });
      if (
        result &&
        typeof (result as PromiseLike<unknown>).then === "function"
      ) {
        void Promise.resolve(result).catch((error) => {
          dependencies.warn("[LobbyAds] Playwire request failed", {
            adLayout,
            error,
          });
        });
      }
      return true;
    } catch (error) {
      dependencies.warn("[LobbyAds] Playwire request failed", {
        adLayout,
        error,
      });
      return false;
    }
  };

  return {
    start(adLayout: LobbyAdLayout, countPageview: boolean) {
      let cancelled = false;
      let initialRequestStarted = false;
      let readyTimer: Timer | null = null;
      let retryTimer: Timer | null = null;
      let retryResultTimer: Timer | null = null;
      const startedAt = dependencies.now();
      const unit = getLobbyPlaywireUnit(adLayout);

      const report = (
        status:
          | "provider_timeout"
          | "initial_filled"
          | "initial_empty"
          | "retry_filled"
          | "retry_empty",
      ) => {
        dependencies.report({
          status,
          layout: adLayout,
          selectorId: unit.selectorId,
          unitType: unit.type,
          elapsedMs: dependencies.now() - startedAt,
        });
      };

      const scheduleFillCheck = () => {
        retryTimer = dependencies.schedule(() => {
          if (cancelled) return;
          if (dependencies.hasMountedAd(unit.selectorId)) {
            report("initial_filled");
            return;
          }

          report("initial_empty");

          dependencies.warn("[LobbyAds] Playwire slot empty; retrying once", {
            adLayout,
            selectorId: unit.selectorId,
          });
          // A retry must never count as a second page view.
          void request(adLayout, false);

          retryResultTimer = dependencies.schedule(() => {
            if (cancelled) return;
            report(
              dependencies.hasMountedAd(unit.selectorId)
                ? "retry_filled"
                : "retry_empty",
            );
          }, PLAYWIRE_LOBBY_RETRY_DELAY_MS);
        }, PLAYWIRE_LOBBY_RETRY_DELAY_MS);
      };

      const runInitialRequest = () => {
        if (cancelled || initialRequestStarted) return;
        initialRequestStarted = true;
        if (readyTimer) {
          dependencies.cancel(readyTimer);
          readyTimer = null;
        }

        const requested = request(adLayout, countPageview);
        if (cancelled || !requested) return;
        scheduleFillCheck();
      };

      const readySpaAds = dependencies.getSpaAds() || cachedSpaAds;
      if (readySpaAds) {
        cachedSpaAds = readySpaAds;
        runInitialRequest();
      } else {
        const queued = dependencies.enqueueWhenReady(() => {
          runInitialRequest();
        });

        if (!queued) {
          dependencies.warn("[LobbyAds] Playwire readiness queue unavailable", {
            adLayout,
            selectorId: unit.selectorId,
          });
        }

        readyTimer = dependencies.schedule(() => {
          if (cancelled || initialRequestStarted) return;
          report("provider_timeout");
          dependencies.warn("[LobbyAds] Playwire provider not ready", {
            adLayout,
            selectorId: unit.selectorId,
          });
        }, PLAYWIRE_LOBBY_READY_TIMEOUT_MS);
      }

      return () => {
        cancelled = true;
        if (readyTimer) dependencies.cancel(readyTimer);
        if (retryTimer) dependencies.cancel(retryTimer);
        if (retryResultTimer) dependencies.cancel(retryResultTimer);
      };
    },
  };
}
